/**
 * Profile Inference Engine
 * Pure function module — no React/Supabase dependencies.
 * Infers role family, seniority band, and skills from LinkedIn headline + positions.
 */

const ROLE_FAMILIES = [
  'Engineering', 'Design', 'Product', 'Data', 'Finance',
  'Marketing', 'Sales', 'Operations', 'HR', 'Legal',
  'Research', 'Executive', 'Other',
];

const SENIORITY_BANDS = [
  'intern', 'junior', 'mid', 'senior', 'lead', 'director', 'vp', 'c_level',
];

// --- Role family keyword mappings (order matters for priority) ---
const ROLE_FAMILY_KEYWORDS = {
  Engineering: [
    'software', 'engineer', 'developer', 'dev ', 'frontend', 'front-end',
    'backend', 'back-end', 'fullstack', 'full-stack', 'full stack',
    'swe', 'sre', 'devops', 'infrastructure', 'platform',
    'firmware', 'flight software', 'embedded', 'architect',
    'development', 'programming', 'coding', 'web developer',
    'mobile developer', 'ios developer', 'android developer',
    'qa engineer', 'test engineer', 'automation engineer',
    'systems engineer', 'network engineer', 'cloud engineer',
    'security engineer', 'devsecops',
  ],
  Design: [
    'designer', 'ux ', 'ui ', 'ux/', 'ui/', 'design',
    'creative director', 'visual', 'graphic', 'interaction',
    'user experience', 'user interface', 'art director',
  ],
  Product: [
    'product manager', 'product lead', 'product owner',
    'product director', 'product vp', 'head of product',
    'chief product', 'product strategy',
  ],
  Data: [
    'data scientist', 'data analyst', 'data engineer',
    'analytics', 'machine learning', ' ml ', 'artificial intelligence',
    ' ai ', 'business intelligence', 'bi analyst', 'data',
  ],
  Finance: [
    'finance', 'accounting', 'accountant', 'cfo',
    'controller', 'treasurer', 'financial', 'audit',
    'tax', 'bookkeeper', 'payroll',
  ],
  Marketing: [
    'marketing', 'brand ', 'growth', 'content',
    'seo', 'digital marketing', 'communications',
    'social media', 'demand gen', 'public relations',
    'copywriter', 'email marketing',
  ],
  Sales: [
    'sales', 'account executive', 'business development',
    'bdr', 'sdr', 'revenue', 'account manager',
    'customer success', 'partnerships',
  ],
  Operations: [
    'operations', ' ops', 'supply chain', 'logistics',
    'project manager', 'program manager', 'scrum master',
    'agile coach', 'procurement', 'facilities',
  ],
  HR: [
    'human resources', ' hr ', 'people ', 'talent',
    'recruiter', 'recruiting', 'people partnership',
    'workforce', 'compensation', 'benefits',
    'organizational development', 'learning & development',
    'people operations', 'people partner',
  ],
  Legal: [
    'legal', 'lawyer', 'attorney', 'counsel',
    'compliance', 'paralegal', 'regulatory',
    'intellectual property', 'patent',
  ],
  Research: [
    'research', 'scientist', 'r&d', ' lab ',
    'laboratory', 'clinical', 'biotech',
    'pharmaceutical',
  ],
  Executive: [
    'ceo', 'coo', 'president', 'founder',
    'co-founder', 'general manager', 'managing director',
    'chief executive', 'chief operating',
  ],
};

// --- Seniority keyword mappings (check most specific first) ---
const SENIORITY_KEYWORDS = [
  { band: 'c_level', keywords: ['ceo ', 'cto ', 'cfo ', 'coo ', 'cio ', 'cmo ', 'cpo ', 'cro ', 'chief ', 'c-suite'] },
  { band: 'vp', keywords: [' vp ', 'vp ', ' vp,', 'vice president', 'v.p.'] },
  { band: 'director', keywords: ['director'] },
  { band: 'lead', keywords: [' lead', 'manager', 'head of', 'team lead', 'principal', 'staff '] },
  { band: 'senior', keywords: ['senior', 'sr.', 'sr '] },
  { band: 'junior', keywords: ['junior', 'jr.', 'jr '] },
  { band: 'intern', keywords: ['intern', 'internship', 'co-op', 'coop'] },
];

// Handle "Executive" titles that imply senior level but not c-level
const EXECUTIVE_TITLE_SENIORITY = 'senior';

// --- Skills keyword mappings ---
const SKILL_KEYWORDS = {
  'Software Development': ['software', 'developer', 'programming', 'coding', 'engineer'],
  'JavaScript': ['javascript', ' js ', 'node', 'react', 'vue', 'angular'],
  'Python': ['python'],
  'React': ['react'],
  'AWS': ['aws', 'amazon web services'],
  'Cloud Computing': ['cloud', 'azure', 'gcp', 'google cloud'],
  'Machine Learning': ['machine learning', 'ml ', 'deep learning'],
  'Data Analysis': ['data analyst', 'analytics', 'data analysis', 'sql'],
  'Leadership': ['lead', 'manager', 'director', 'vp ', 'head of', 'chief'],
  'Management': ['manager', 'management', 'managing'],
  'Product Management': ['product manager', 'product owner', 'product lead'],
  'UX Design': ['ux', 'user experience', 'usability'],
  'UI Design': ['ui', 'user interface', 'visual design'],
  'DevOps': ['devops', 'ci/cd', 'infrastructure', 'sre'],
  'Agile': ['agile', 'scrum', 'kanban', 'sprint'],
  'Sales': ['sales', 'revenue', 'business development'],
  'Marketing': ['marketing', 'growth', 'brand', 'seo', 'content'],
  'Finance': ['finance', 'accounting', 'financial'],
  'Recruiting': ['recruiting', 'recruiter', 'talent acquisition', 'hiring'],
  'People Operations': ['people operations', 'people partnership', 'human resources', 'hr '],
  'Project Management': ['project manager', 'program manager', 'pmp'],
  'Security': ['security', 'cybersecurity', 'infosec'],
  'Mobile Development': ['mobile', 'ios', 'android', 'react native', 'flutter'],
  'Database': ['database', 'sql', 'postgres', 'mysql', 'mongodb'],
  'Embedded Systems': ['embedded', 'firmware', 'flight software', 'autonomous'],
  'Aerospace': ['aerospace', 'flight', 'autonomous', 'airpower', 'defense'],
  'Legal': ['legal', 'compliance', 'regulatory', 'attorney'],
  'Research': ['research', 'r&d', 'scientist'],
  'Communication': ['communications', 'public relations', 'writing'],
  'Strategy': ['strategy', 'strategic', 'planning'],
};

/**
 * Normalize text for keyword matching.
 * @param {string} text
 * @returns {string}
 */
function normalize(text) {
  if (!text) return '';
  return ` ${text.toLowerCase().trim()} `;
}

/**
 * Gather all searchable text from headline and positions.
 * @param {string} headline
 * @param {Array} positions
 * @returns {string[]} Array of normalized texts, headline first
 */
function gatherTexts(headline, positions) {
  const texts = [];
  if (headline) texts.push(normalize(headline));
  if (positions && Array.isArray(positions)) {
    for (const pos of positions) {
      if (pos.title) texts.push(normalize(pos.title));
    }
  }
  return texts;
}

/**
 * Infer role family from headline and position titles.
 * Checks headline first, then position titles as fallback.
 * @param {string} headline - LinkedIn headline
 * @param {Array<{title: string, company: string}>} positions - Work history
 * @returns {string} One of ROLE_FAMILIES
 */
function inferRoleFamily(headline, positions) {
  const texts = gatherTexts(headline, positions);

  for (const text of texts) {
    // Check each role family's keywords
    for (const [family, keywords] of Object.entries(ROLE_FAMILY_KEYWORDS)) {
      for (const keyword of keywords) {
        if (text.includes(keyword.toLowerCase())) {
          return family;
        }
      }
    }
  }

  return 'Other';
}

/**
 * Infer seniority band from headline and position titles.
 * Checks headline first, then position titles as fallback.
 * @param {string} headline - LinkedIn headline
 * @param {Array<{title: string, company: string}>} positions - Work history
 * @returns {string} One of SENIORITY_BANDS
 */
function inferSeniorityBand(headline, positions) {
  const texts = gatherTexts(headline, positions);

  for (const text of texts) {
    for (const { band, keywords } of SENIORITY_KEYWORDS) {
      for (const keyword of keywords) {
        if (text.includes(keyword.toLowerCase())) {
          // Special case: "Executive" in title without c-level prefix
          // means senior-level, not c_level
          if (band === 'c_level' && !keywords.some(k => k !== 'chief ' && text.includes(k))) {
            continue;
          }
          return band;
        }
      }
    }

    // Check for "Executive" in title (implies senior, not c-level)
    if (text.includes('executive') && !SENIORITY_KEYWORDS[0].keywords.some(k => text.includes(k))) {
      return EXECUTIVE_TITLE_SENIORITY;
    }
  }

  return 'mid';
}

/**
 * Infer skill tags from headline and position titles.
 * @param {string} headline - LinkedIn headline
 * @param {Array<{title: string, company: string}>} positions - Work history
 * @returns {string[]} Up to 10 skill tags
 */
function inferSkills(headline, positions) {
  const texts = gatherTexts(headline, positions);
  const allText = texts.join(' ');
  const skills = new Set();

  for (const [skill, keywords] of Object.entries(SKILL_KEYWORDS)) {
    for (const keyword of keywords) {
      if (allText.includes(keyword.toLowerCase())) {
        skills.add(skill);
        break;
      }
    }
    if (skills.size >= 10) break;
  }

  return Array.from(skills).slice(0, 10);
}

/**
 * Infer complete profile metadata from headline and positions.
 * Convenience wrapper combining all three inference functions.
 * @param {string} headline - LinkedIn headline
 * @param {Array<{title: string, company: string}>} positions - Work history
 * @returns {{ role_family: string, seniority_band: string, skills: string[] }}
 */
function inferProfile(headline, positions) {
  return {
    role_family: inferRoleFamily(headline, positions),
    seniority_band: inferSeniorityBand(headline, positions),
    skills: inferSkills(headline, positions),
  };
}

module.exports = {
  ROLE_FAMILIES,
  SENIORITY_BANDS,
  inferRoleFamily,
  inferSeniorityBand,
  inferSkills,
  inferProfile,
};
